import { Address, EmploymentType } from './Account';

export enum IncomeEvidence {
  SA302 = 'SA302',
  AccountantCertificate = 'Accountant certificate',
  AuditedAccounts = 'Audited annual accounts',
  RemittanceSlips = 'Remittance slips',
  Payslips = 'Payslips',
}

export enum ContractType {
  Permanent = 'Permanent',
  Rolling = 'Rolling',
  FixedTerm = 'Fixed term',
  Temporary = 'Temporary',
  Agency = 'Agency',
}

export enum NotWorkingStatus {
  ParentalLeaveSMP = 'Parental leave (SMP)',
  Unemployed = 'Unemployed',
  Student = 'Student',
  HousePerson = 'House person',
}

export enum OtherIncomeType {
  PrivatePension = 'Private pension',
  StatePension = 'State pension',
  ChildBenefit = 'Child benefit',
  ChildTaxCredit = 'Child tax credit',
  WorkingTaxCredit = 'Working tax credit',
  EmploymentSupportAllowance = 'Employment Support Allowance (ESA)',
  DisabilityLivingAllowance = 'Disability living allowance',
  PersonalIndependencePayment = 'Personal Independence Payment (PIP)',
  CarersAllowance = "Carer's allowance",
  UniversalCredit = 'Universal credit',
  RentalIncome = 'Rental income',
  LodgerIncome = 'Lodger income',
  MaintenanceCSACourtOrdered = 'Maintenance/CSA (Court ordered)',
  MaintenanceCSANotCourtOrdered = 'Maintenance/CSA (Not court ordered)',
}

export enum Frequency {
  Annually = 'Annually',
  BiAnnually = 'Bi-annually',
  Quarterly = 'Quarterly',
  Monthly = 'Monthly',
  FourWeekly = 'Four-weekly',
  Fortnightly = 'Fortnightly',
  Weekly = 'Weekly',
}

export enum Segment {
  Employed = 'Employed',
  ConstructionIndustryScheme = 'Construction Industry Scheme',
  DirectorOfALimitedCompany = 'Director Of A Limited Company',
  SoleTrader = 'Sole Trader',
  LimitedLiabilityPartnership = 'Limited Liability Partnership',
  Partnership = 'Partnership',
  FosterCarer = 'Foster Carer',
  NotWorkingRetired = 'Not Working Retired',
  NotWorkingNotRetired = 'Not Working Not Retired',
  SA302 = 'SA302',
  Payslips = 'Payslips',
  RemittanceSlips = 'Remittance Slips',
  AccountantCertificate = 'Accountant Certificate',
  AuditedAccount = 'Audited Account',
  NonJobRelatedIncome = 'Non Job Related Income',
}

export type PayslipsEvidence = {
  id: string,
  grossAnnualIncome: number,
  netMonthlyIncome: number,
}

export type EmployerDetails = {
  id: string,
  employerName: string,
  address: Address,
}

export type FinancialYearBase = {
  id: string,
  financialYearEnd: string | null,
  grossAnnualSalary: number | null,
  annualDividends: number | null,
}

export type FinancialYear = FinancialYearBase & {
  netMonthlyTakeHome: number | null,
}

export type FinancialYearSet = {
  id: string,
  latestCompletedYear: FinancialYear,
  previousCompletedYear: FinancialYearBase,
  currentYearProjection: FinancialYearBase,
}

export type PartnershipFinancialYearBase = {
  id: string,
  financialYearEnd: string | null,
  totalGrossProfitBeforeTax: number | null,
  totalNetProfitAfterTax: number | null,
}

export type PartnershipFinancialYear = PartnershipFinancialYearBase & {
  customerNetMonthlyTakeHome: number | null,
}

export type PartnershipFinancialYearSet = {
  id: string,
  latestCompletedYear: PartnershipFinancialYear,
  previousCompletedYear: PartnershipFinancialYearBase,
  currentYearProjection: PartnershipFinancialYearBase,
}

export type SoleTraderFinancialYearBase = {
  id: string,
  financialYearEnd: string | null,
  grossProfitBeforeTax: number | null,
  netProfitAfterTax: number | null,
}

export type SoleTraderFinancialYear = SoleTraderFinancialYearBase & {
  netMonthlyTakeHome: number | null,
}

export type SoleTraderFinancialYearSet = {
  id: string,
  latestCompletedYear: SoleTraderFinancialYear,
  previousCompletedYear: SoleTraderFinancialYearBase,
  currentYearProjection: SoleTraderFinancialYearBase,
}

export type SA302Evidence = PayslipsEvidence & {
  deductions: number,
}

export type EmploymentHistoryEntryBase = {
  id: string,
  ordinal: number,
  startDate: string | null,
  endDate: string | null,
  employmentType?: keyof typeof EmploymentType | null | 'None',
  contentHash: string | null,
}

export interface EmploymentHistoryEntry extends EmploymentHistoryEntryBase {
  employmentType?: 'None' | null,
}

export type EmploymentWithIncome = EmploymentHistoryEntryBase & {
  currentlyWorkingInThisRole: boolean,
  evidenceType: keyof typeof IncomeEvidence,
  possibleEvidenceTypes: (keyof typeof IncomeEvidence)[],
}

export interface EmployedOrCIS extends EmploymentWithIncome {
  employmentType: 'Employed' | 'ConstructionIndustryScheme',
  jobTitleCode: string,
  industryCode: string,
  contractType: keyof typeof ContractType,
  isZeroHoursContract: boolean,
  payslipsEvidence: PayslipsEvidence,
  employer: EmployerDetails,
}

export interface DirectorOfLimitedCompanyEntry extends EmploymentWithIncome {
  employmentType: 'DirectorOfLimitedCompany',
  isShareholder: boolean,
  shareholdingPercentage: number,
  companyName: string,
  companyRegistrationNumber: string,
  industryCode: string,
  sA302Evidence: SA302Evidence,
  payslipsEvidence: PayslipsEvidence,
  accountantCertificateEvidence: FinancialYearSet,
}

export interface FosterCarerEntry extends EmploymentWithIncome {
  employmentType: 'FosterCarer',
  agencyName: string,
  sA302Evidence: SA302Evidence,
  remittanceSlipsEvidence: PayslipsEvidence,
}

export interface LimitedLiabilityPartnershipEntry extends EmploymentWithIncome {
  employmentType: 'LimitedLiabilityPartnership',
  companyName: string,
  companyRegistrationNumber: string,
  industryCode: string,
  shareOfOwnership: number,
  sA302Evidence: SA302Evidence,
  payslipsEvidence: PayslipsEvidence,
  accountantCertificateEvidence: FinancialYearSet,
}

export interface PartnershipEntry extends EmploymentWithIncome {
  employmentType: 'Partnership',
  companyName: string,
  industryCode: string,
  address: Address;
  sA302Evidence: SA302Evidence,
  payslipsEvidence: PayslipsEvidence,
  accountantCertificateEvidence: PartnershipFinancialYearSet,
}

export interface SoleTraderEntry extends EmploymentWithIncome {
  employmentType: 'SoleTrader',
  tradingName: string,
  industryCode: string,
  isTradingAddressSameAsSecurityAddress: boolean,
  address: Address,
  sA302Evidence: SA302Evidence,
  accountantCertificateEvidence: SoleTraderFinancialYearSet,
  auditedAccountsEvidence: SoleTraderFinancialYearSet,
}

export interface NotWorkingRetiredEntry extends EmploymentHistoryEntryBase {
  employmentType: 'NotWorkingRetired',
  currentlyNotWorking: boolean,
}

export interface NotWorkingNotRetiredEntry extends EmploymentHistoryEntryBase {
  employmentType: 'NotWorkingNotRetired',
  currentlyNotWorking: boolean,
  status: keyof typeof NotWorkingStatus,
}

export type EmploymentHistory = EmploymentHistoryEntry
  | EmployedOrCIS
  | DirectorOfLimitedCompanyEntry
  | FosterCarerEntry
  | LimitedLiabilityPartnershipEntry
  | PartnershipEntry
  | SoleTraderEntry
  | NotWorkingRetiredEntry
  | NotWorkingNotRetiredEntry;

export type OtherIncome = {
  id: string;
  ordinal: number;
  incomeType: keyof typeof OtherIncomeType;
  amount: number;
  frequency: keyof typeof Frequency;
  description: string;
};

export type VersionInfo = {
  segment: keyof typeof Segment;
  version: number;
}

export type IncomeAndEmployment = {
  id: string,
  employmentHistory: EmploymentHistory[],
  otherIncomes: OtherIncome[],
  versionInfo: VersionInfo[],
}
